import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';

import dayjs from 'dayjs';
import { listContact } from '../api/actions/query';
import { remove } from '../api/actions/mutation';
import { messageShow } from '../../common/api/actions';
import BrandedLoading from '../../common/BrandedLoading';
import EmptyMessage from '../../common/EmptyMessage';
import SectionPaper from '../../common/SectionPaper';
import routes from '../../../setup/routes';

const getLeadStatusBadge = (status = 'Contact') => {
  switch (status) {
    case 'Customer':
      return { bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' };
    case 'Prospect':
      return { bgcolor: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' };
    case 'Lead':
      return { bgcolor: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd' };
    case 'Contact':
    default:
      return { bgcolor: '#f8fafc', color: '#0d3559', border: '1px solid #e2e8f0' };
  }
};

class CrmUnsubscribed extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      contacts: [],
      filteredContacts: [],
      searchQuery: '',
      deleteConfirmOpen: false,
      contactToDelete: null,
    };
  }

  componentDidMount() {
    this.refresh();
  }

  refresh = async () => {
    const { listContact, messageShow } = this.props;
    this.setState({ isLoading: true });

    try {
      const { data } = await listContact();
      if (data.success && data.data) {
        const allContacts = Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.data?.list)
          ? data.data.list
          : [];
        const unsubscribed = allContacts.filter(
          (c) => c && c.emailSubscription === false
        );

        this.setState({
          contacts: unsubscribed,
          filteredContacts: this.filterList(unsubscribed, this.state.searchQuery),
        });
      } else {
        messageShow(data.message || 'Failed to fetch CRM contacts.');
      }
    } catch (error) {
      messageShow('Error fetching unsubscribed CRM contacts.');
    } finally {
      this.setState({ isLoading: false });
    }
  };

  filterList = (list, query) => {
    const safeList = Array.isArray(list) ? list : [];
    if (!query || !query.trim()) return safeList;
    const q = query.toLowerCase().trim();
    return safeList.filter((c) => {
      const name = `${c.contactName || ''} ${c.storeName || ''} ${c.name || ''} ${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
      const email = (c.email || '').toLowerCase();
      const company = `${c.storeName || ''} ${c.companyName || ''}`.toLowerCase();
      const stage = (c.leadStatus || '').toLowerCase();
      const phone = (c.phoneNumber || c.phone || c.mobile || '').toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        company.includes(q) ||
        stage.includes(q) ||
        phone.includes(q)
      );
    });
  };

  handleSearchChange = (e) => {
    const query = e.target.value;
    this.setState((prevState) => ({
      searchQuery: query,
      filteredContacts: this.filterList(prevState.contacts, query),
    }));
  };

  handleOpenDeleteConfirm = (contact) => {
    this.setState({
      deleteConfirmOpen: true,
      contactToDelete: contact,
    });
  };

  handleCloseDeleteConfirm = () => {
    this.setState({
      deleteConfirmOpen: false,
      contactToDelete: null,
    });
  };

  handleConfirmDelete = async () => {
    const { contactToDelete } = this.state;
    if (!contactToDelete) return;

    const { messageShow } = this.props;
    try {
      const response = await remove({ contactId: contactToDelete._id });
      if (response && response.data && response.data.success) {
        messageShow(response.data.message || 'Contact soft-deleted successfully.');
        this.setState((prevState) => {
          const updated = prevState.contacts.filter((c) => c._id !== contactToDelete._id);
          return {
            contacts: updated,
            filteredContacts: this.filterList(updated, prevState.searchQuery),
            deleteConfirmOpen: false,
            contactToDelete: null,
          };
        });
      } else {
        messageShow(response?.data?.message || 'Failed to soft-delete contact.');
        this.handleCloseDeleteConfirm();
      }
    } catch (error) {
      messageShow('Some error occurred while deleting contact.');
      this.handleCloseDeleteConfirm();
    }
  };

  exportCSV = () => {
    const { contacts } = this.state;
    if (!contacts || contacts.length === 0) return;

    const headers = ['Name', 'Email', 'Phone', 'Lead Stage', 'Company', 'Email Status', 'Created At'];
    const rows = contacts.map((c) => [
      `"${c.contactName || c.storeName || c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || '-'}"`,
      `"${c.email || ''}"`,
      `"${c.phoneNumber || c.phone || c.mobile || ''}"`,
      `"${c.leadStatus || 'Contact'}"`,
      `"${c.storeName || c.companyName || ''}"`,
      `"Unsubscribed"`,
      `"${c.createdAt ? dayjs(c.createdAt).format('YYYY-MM-DD HH:mm') : ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `crm_email_unsubscribed_list_${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { history } = this.props;
    const {
      isLoading,
      contacts,
      filteredContacts,
      searchQuery,
      deleteConfirmOpen,
      contactToDelete,
    } = this.state;

    return (
      <Container fluid style={{ padding: '20px' }}>
        {/* Top Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push(routes.listContacts.path)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#cbd5e1',
                color: '#475569',
                fontWeight: 600,
                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
              }}
            >
              Back to CRM
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0d3559' }}>
              Email Unsubscribed List — CRM Contacts
            </Typography>
            <Chip
              size="small"
              label={`${contacts.length} Unsubscribed`}
              sx={{
                bgcolor: '#fee2e2',
                color: '#dc2626',
                fontWeight: 700,
                fontSize: '0.8rem',
              }}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={this.exportCSV}
            disabled={contacts.length === 0}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Export Unsubscribed CSV
          </Button>
        </Box>

        {/* Regulatory Notice Banner */}
        <Box
          sx={{
            p: 2,
            bgcolor: '#fef2f2',
            border: '1px solid #fee2e2',
            borderRadius: '12px',
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <UnsubscribeIcon sx={{ color: '#ef4444', fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#991b1b' }}>
              Email Unsubscribed Registry (CRM)
            </Typography>
            <Typography variant="body2" sx={{ color: '#b91c1c', fontSize: '0.85rem' }}>
              All leads, prospects, and contacts in this view have opted out of marketing emails. They are automatically excluded from campaigns and broadcast lists.
            </Typography>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by name, email, company, or stage..."
            value={searchQuery}
            onChange={this.handleSearchChange}
            sx={{
              width: { xs: '100%', sm: 380 },
              '& .MuiOutlinedInput-root': { borderRadius: '8px' },
            }}
          />
          {searchQuery && (
            <Button
              variant="text"
              size="small"
              onClick={() =>
                this.setState((prev) => ({
                  searchQuery: '',
                  filteredContacts: prev.contacts,
                }))
              }
              sx={{ textTransform: 'none', color: '#64748b' }}
            >
              Clear Filter
            </Button>
          )}
        </Box>

        {/* Table View */}
        <SectionPaper>
          {isLoading ? (
            <BrandedLoading />
          ) : filteredContacts.length === 0 ? (
            <EmptyMessage
              message={
                searchQuery
                  ? 'No unsubscribed contacts match your search query.'
                  : 'No unsubscribed contacts found in CRM.'
              }
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table padding="normal">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Email ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Lead Stage</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Company / Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Created Date</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#334155' }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: '#334155' }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts.map((contact) => {
                    const displayName =
                      contact.contactName ||
                      contact.storeName ||
                      contact.name ||
                      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                      contact.email ||
                      '-';
                    const secondaryName =
                      contact.contactName &&
                      contact.storeName &&
                      contact.storeName.trim().toLowerCase() !== contact.contactName.trim().toLowerCase()
                        ? contact.storeName
                        : null;
                    const stageBadge = getLeadStatusBadge(contact.leadStatus);

                    return (
                      <TableRow key={contact._id} hover>
                        <TableCell>
                          <Link
                            to={routes.viewById.path(contact._id)}
                            style={{
                              textDecoration: 'none',
                              color: '#0d3559',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            {displayName}
                            <LaunchIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                          </Link>
                          {secondaryName && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', color: '#64748b', fontSize: '11px', mt: 0.3 }}
                            >
                              {secondaryName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{contact.email || '-'}</span>
                          </Box>
                        </TableCell>
                        <TableCell>{contact.phoneNumber || contact.phone || contact.mobile || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={contact.leadStatus || 'Contact'}
                            sx={{
                              ...stageBadge,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {contact.storeName || contact.companyName || contact.customerType || '-'}
                        </TableCell>
                        <TableCell>
                          {contact.createdAt
                            ? dayjs(contact.createdAt).format('DD MMM YYYY')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label="UNSUBSCRIBED"
                            sx={{
                              bgcolor: '#fee2e2',
                              color: '#dc2626',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              letterSpacing: '0.5px',
                              borderRadius: '6px',
                              height: 22,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Soft Delete Contact">
                            <IconButton
                              size="small"
                              onClick={() => this.handleOpenDeleteConfirm(contact)}
                              sx={{
                                color: '#ef4444',
                                '&:hover': { bgcolor: '#fee2e2' },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </SectionPaper>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteConfirmOpen} onClose={this.handleCloseDeleteConfirm}>
          <DialogTitle sx={{ fontWeight: 700, color: '#0d3559' }}>
            Confirm Soft Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#475569' }}>
              Are you sure you want to soft delete{' '}
              <strong>
                {contactToDelete?.contactName ||
                  contactToDelete?.storeName ||
                  contactToDelete?.name ||
                  contactToDelete?.firstName ||
                  contactToDelete?.email ||
                  'this contact'}
              </strong>
              ?
              <br />
              This will safely mark the record as deleted (<code>isDeleted: true</code>) without breaking history.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={this.handleCloseDeleteConfirm}
              sx={{ textTransform: 'none', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={this.handleConfirmDelete}
              sx={{
                bgcolor: '#dc2626',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
              }}
            >
              Soft Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }
}

CrmUnsubscribed.propTypes = {
  listContact: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
};

export default withRouter(
  connect(null, { listContact, messageShow })(CrmUnsubscribed)
);
